import React, { useState, useEffect, useContext } from 'react';
import { IterationContext, IterationContextType } from '../../contexts/IterationContext';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { getAttributeValues } from '../../services/IterationServices';
import Utils from 'Common/Utils';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Add, Minus } from 'Components/DesignSystem/Iconography/Icons';
import { LAYERS } from 'Components/DesignSystem/layers';

type FilterModalProps = {
    isOpen: boolean;
    conditionId: string;
    onClose: () => void;
};

const COMPARISON_OPTIONS = [
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
    { value: '>', label: '>' },
    { value: '<', label: '<' },
    { value: '>=', label: '>=' },
    { value: '<=', label: '<=' },
];

const LOGICAL_OPTIONS = [
    { value: 'AND', label: 'And' },
    { value: 'OR', label: 'Or' },
];

/**
 * The per-condition Add Filters dialog, on the DS Dialog (2026-09-08) — the
 * okf/rules § 4.4 recipe. Every draft-state handler is byte-identical: the
 * component stays mounted inside `Condition` and early-returns while closed, so
 * the draft `filters` state survives exactly as before (the init effect re-seeds
 * on open); `applyFilters` still diffs adds/updates/removals against the
 * condition's saved filters. Attribute and comparison pickers are DS `Dropdown`s
 * (`usePortal` — the rows live in the Dialog's overflow-y-auto content); the
 * attribute-VALUE picker stays react-select (API-paged lazy loading has no DS
 * equivalent yet — § 210) with the § 4.4 token `customStyles` + body portal so
 * dark themes it and the menu clears the footer band. +/− are DS IconButtons
 * (§ 212/§ 238); the row testids are index-templated (the old static
 * `iteration-filtermodal-select-2/-3` duplicated per row).
 */
const FilterModal: React.FC<FilterModalProps> = ({
    isOpen,
    conditionId,
    onClose
}) => {
    const {
        iterationPayload,
        activeIterationId,
        attributeList,
        addFilter,
        removeFilter,
        flattenConditions,
        updateFilter
    } = useContext<IterationContextType>(IterationContext);

    const [filters, setFilters] = useState<Filter[]>([]);

    // Get current condition and its filters
    const currentIteration = iterationPayload?.iterations?.find(i => i.id === activeIterationId);
    const operationConditions = currentIteration ? flattenConditions(currentIteration.conditions) : [];
    const condition = operationConditions.find(c => c.id === conditionId);
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    useEffect(() => {
        const hasIncompleteFilter = filters.some(filter => {
            return filter.attribute_id && (!filter.attribute_value_id || filter.attribute_value_id.trim() === '');
        });
        setIsSaveDisabled(hasIncompleteFilter);
    }, [filters]);

    // Initialize filters when modal opens
    useEffect(() => {
        if (isOpen && condition) {
            if (condition.filters.length > 0) {
                setFilters([...condition.filters]);
            } else {
                // Add one empty filter if none exist
                setFilters([{
                    id: `new-${Date.now()}`,
                    attribute_id: '',
                    attribute_name: '',
                    comparison_rule: '==',
                    attribute_value: '',
                    logical_operator: 'AND'
                }]);
            }
        }
    }, [isOpen, conditionId]);

    // Helper function to get already selected attribute value IDs for a given attribute
    const getSelectedAttributeValueIds = (attributeId: string, excludeFilterIndex?: number): string[] => {
        return filters
            .filter((filter, index) =>
                filter.attribute_id === attributeId &&
                filter.attribute_value_id &&
                (excludeFilterIndex === undefined || index !== excludeFilterIndex)
            )
            .map(filter => filter.attribute_value_id)
            .filter(Boolean) as string[];
    };

    const addNewFilter = () => {
        const newFilter: Filter = {
            id: `new-${Utils.generate_guid()}`,
            attribute_id: '',
            attribute_name: '',
            comparison_rule: '==',
            attribute_value: '',
            logical_operator: 'AND'
        };

        setFilters([...filters, newFilter]);
    };

    const removeFilterAtIndex = (index: number) => {
        if (filters.length > 1) {
            const newFilters = filters.filter((_, i) => i !== index);
            setFilters(newFilters);
        }
    };

    const updateFilterAtIndex = (index: number, updates: Partial<Filter>) => {
        const newFilters = [...filters];
        newFilters[index] = { ...newFilters[index], ...updates };
        setFilters(newFilters);
    };

    const clearFilters = () => {
        setFilters([{
            id: `new-${Date.now()}`,
            attribute_id: '',
            attribute_name: '',
            comparison_rule: '==',
            attribute_value: '',
            logical_operator: 'AND'
        }]);
    };

    const applyFilters = () => {
        if (!activeIterationId) return;

        // Get current filters from the condition
        const currentFilters = condition?.filters || [];

        filters.forEach(filter => {
            if (filter.attribute_id) {
                // Check if this is an existing filter (has an ID that doesn't start with 'new-')
                const isExistingFilter = filter.id && !filter.id.startsWith('new-');

                if (isExistingFilter) {
                    // Update existing filter
                    updateFilter(activeIterationId, conditionId, filter.id, {
                        attribute_id: filter.attribute_id,
                        attribute_name: filter.attribute_name,
                        comparison_rule: filter.comparison_rule,
                        attribute_value_id: filter.attribute_value_id,
                        attribute_value: filter.attribute_value,
                        logical_operator: filter.logical_operator
                    });
                } else {
                    // Add new filter
                    addFilter(activeIterationId, conditionId, {
                        attribute_id: filter.attribute_id,
                        attribute_name: filter.attribute_name,
                        comparison_rule: filter.comparison_rule,
                        attribute_value_id: filter.attribute_value_id,
                        attribute_value: filter.attribute_value,
                        logical_operator: filter.logical_operator
                    });
                }
            }
        });

        // Remove filters that were deleted (present in current but not in filters state)
        const currentFilterIds = new Set(filters.map(f => f.id));
        currentFilters.forEach(currentFilter => {
            if (!currentFilterIds.has(currentFilter.id)) {
                removeFilter(activeIterationId, conditionId, currentFilter.id);
            }
        });

        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog
            isOpen
            onClose={onClose}
            title="Add Filters"
            size="lg"
            data-testid="filter-modal"
            footer={
                <div className="tw-flex tw-w-full tw-justify-end tw-gap-3">
                    <Button
                        variant="tertiary"
                        onClick={clearFilters}
                        data-testid="clear-filters-button"
                    >
                        Clear Filters
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        data-testid="close-button"
                    >
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={applyFilters}
                        disabled={isSaveDisabled}
                        data-testid="apply-filters-button"
                    >
                        Save Filters
                    </Button>
                </div>
            }
        >
            {/* Column Headers */}
            <div className="tw-flex tw-items-center tw-gap-6 tw-mb-2">
                <div className="tw-w-12"></div>
                <div className="tw-w-60">
                    <Typography variant="title" as="h3" className="tw-text-ink-secondary">Attribute</Typography>
                </div>
                <div className="tw-w-36">
                    <Typography variant="title" as="h3" className="tw-text-ink-secondary">Comparison Rule</Typography>
                </div>
                <div className="tw-w-60">
                    <Typography variant="title" as="h3" className="tw-text-ink-secondary">Attribute Value</Typography>
                </div>
                <div className="tw-w-16"></div>
            </div>

            {/* Filter Rows */}
            <div className="tw-space-y-4">
                {filters.map((filter, index) => (
                    <React.Fragment key={filter.id}>
                        <FilterRow
                            filter={filter}
                            index={index}
                            attributeList={attributeList}
                            onUpdate={(updates) => updateFilterAtIndex(index, updates)}
                            onRemove={() => removeFilterAtIndex(index)}
                            onAdd={addNewFilter}
                            canRemove={filters.length > 1}
                            getSelectedAttributeValueIds={getSelectedAttributeValueIds}
                        />

                        {/* Logical Operator Row */}
                        {index < filters.length - 1 && (
                            <div className="tw-flex tw-items-center tw-gap-6">
                                <div className="tw-w-12"></div>
                                <div className="tw-w-64">
                                    <div className="tw-w-24">
                                        <Dropdown
                                            label=""
                                            aria-label="Logical operator"
                                            size="sm"
                                            usePortal
                                            options={LOGICAL_OPTIONS}
                                            value={filter.logical_operator || 'AND'}
                                            onChange={(value) => updateFilterAtIndex(index, {
                                                logical_operator: value as LogicalOperator
                                            })}
                                            data-testid={`iteration-filtermodal-select-1-${index}`}
                                        />
                                    </div>
                                </div>
                                <div className="tw-w-24"></div>
                                <div className="tw-w-64"></div>
                                <div className="tw-w-16"></div>
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </Dialog>
    );
};

// Filter Row Component
type FilterRowProps = {
    filter: Filter;
    index: number;
    attributeList: any[];
    onUpdate: (updates: Partial<Filter>) => void;
    onRemove: () => void;
    onAdd: () => void;
    canRemove: boolean;
    getSelectedAttributeValueIds: (attributeId: string, excludeFilterIndex?: number) => string[];
};

const FilterRow: React.FC<FilterRowProps> = ({
    filter,
    index,
    attributeList,
    onUpdate,
    onRemove,
    onAdd,
    canRemove,
    getSelectedAttributeValueIds
}) => {
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [options, setOptions] = useState<{ value: string, label: string }[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [noResults, setNoResults] = useState(false);

    // Fetch attribute values when attribute changes
    useEffect(() => {
        if (filter.attribute_id) {
            fetchAttributeValues(filter.attribute_id, page, searchValue);
        } else {
            resetState();
        }
    }, [filter.attribute_id, page, searchValue]);

    const fetchAttributeValues = async (attributeId: string, page: number, searchString = '') => {
        if (!attributeId) return;

        try {
            setIsLoading(true);
            const res = await getAttributeValues(attributeId, page, searchString);
            if (res?.done) {
                const attributeValues = res?.data[0]?.attribute_values ?? [];
                handleAttributeResponse(attributeValues, attributeId);
            }
        } catch (error) {
            setOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAttributeResponse = (response: any[], attributeId: string) => {
        // Get already selected attribute value IDs for this attribute (excluding current filter)
        const selectedValueIds = getSelectedAttributeValueIds(attributeId, index);

        // Filter out already selected values
        const availableValues = response.filter(item =>
            !selectedValueIds.includes(item.id)
        );

        const newOptions = availableValues.map(item => ({
            value: item.id,
            label: item.value
        }));

        if (page === 1 && filter.attribute_value_id) {
            // Always include the current filter's selected value even if it would normally be filtered out
            const isCurrentValuePresent = newOptions.some(option => option.value === filter.attribute_value_id);
            if (!isCurrentValuePresent) {
                const currentValueOption = {
                    value: filter.attribute_value_id,
                    label: filter.attribute_value || ''
                };
                const updatedOptions = [currentValueOption, ...newOptions];
                setOptions(prevData => (page === 1 ? updatedOptions : [...prevData, ...updatedOptions]));
            } else {
                setOptions(prevData => (page === 1 ? newOptions : [...prevData, ...newOptions]));
            }
        } else {
            setOptions(prevData => (page === 1 ? newOptions : [...prevData, ...newOptions]));
        }

        setNoResults(newOptions.length === 0);
    };

    const loadMoreOptions = async () => {
        if (!noResults) {
            setPage(prevPage => prevPage + 1);
        } else {
            setIsLoading(false);
        }
    };

    const debouncedLoadMoreOptions = React.useCallback(
        debounce(() => loadMoreOptions(), 500),
        [page]
    );

    const debouncedSearch = debounce((value: string) => {
        setSearchValue(value);
        setPage(1);
    }, 500);

    const onInputChange = (input: string) => {
        if (input === '') {
            setPage(1);
        }
        debouncedSearch(input);
    };

    const handleMenuScrollToBottom = () => {
        setIsLoading(true);
        debouncedLoadMoreOptions();
    };

    const resetState = () => {
        setOptions([]);
        setNoResults(false);
        setPage(1);
        setSearchValue('');
    };

    /* Dark pass (§ 4.4): react-select paints its own white control/menu, which no
       data-theme can reach — every value is a --ds-* var with the light value as
       fallback. The menu portals to <body> so it clears the Dialog's footer band;
       the z is the § 204 ladder's top rung, and the font restates Noto because a
       body-portalled surface inherits body's stack (§ 195). Styling only. */
    const customStyles = {
        control: (provided: any) => ({
            ...provided,
            textAlign: 'left',
            backgroundColor: 'var(--ds-surface, #ffffff)',
            borderColor: 'var(--ds-edge-strong, #8891a0)',
        }),
        menu: (provided: any) => ({
            ...provided,
            backgroundColor: 'var(--ds-surface-raised, #ffffff)',
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            textAlign: 'left',
            backgroundColor: state.isFocused ? 'var(--ds-surface-sunken, #f4f5f7)' : 'transparent',
            color: 'var(--ds-ink, #172b4d)',
        }),
        singleValue: (provided: any) => ({ ...provided, color: 'var(--ds-ink, #172b4d)' }),
        input: (provided: any) => ({ ...provided, color: 'var(--ds-ink, #172b4d)' }),
        placeholder: (provided: any) => ({ ...provided, color: 'var(--ds-ink-secondary, #505866)' }),
        menuPortal: (provided: any) => ({
            ...provided,
            zIndex: LAYERS.tooltip,
            fontFamily: '"Noto Sans", sans-serif',
        }),
    };

    return (
        <div className="tw-flex tw-items-center tw-gap-6" data-testid={`filter-row-${index}`}>
            <div className="tw-w-12 tw-flex tw-justify-end">
                <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">when</Typography>
            </div>

            {/* Attribute Dropdown */}
            <div className="tw-w-60">
                <Dropdown
                    label=""
                    aria-label="Filter attribute"
                    usePortal
                    placeholder="Select Attribute"
                    options={attributeList ?? []}
                    value={filter.attribute_id || undefined}
                    onChange={(value) => {
                        const attribute = attributeList.find(attr => attr.value === String(value));
                        onUpdate({
                            attribute_id: String(value),
                            attribute_name: attribute?.label || "",
                            attribute_value_id: '',
                            attribute_value: ''
                        });
                        resetState();
                    }}
                    data-testid={`iteration-filtermodal-select-2-${index}`}
                />
            </div>

            {/* Comparison Rule Dropdown */}
            <div className="tw-w-36">
                <Dropdown
                    label=""
                    aria-label="Comparison rule"
                    usePortal
                    options={COMPARISON_OPTIONS}
                    value={filter.comparison_rule}
                    onChange={(value) => onUpdate({ comparison_rule: value as ComparisonOperator })}
                    data-testid={`iteration-filtermodal-select-3-${index}`}
                />
            </div>

            {/* Attribute Value Select with Search */}
            <div className="tw-w-60">
                <Select
                    options={options}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    onMenuScrollToBottom={handleMenuScrollToBottom}
                    isLoading={isLoading}
                    onInputChange={onInputChange}
                    value={filter.attribute_value_id ? { value: filter.attribute_value_id, label: filter.attribute_value } : null}
                    onChange={(selectedOption: any) => {
                        onUpdate({
                            attribute_value_id: selectedOption?.value || '',
                            attribute_value: selectedOption?.label || ''
                        });
                    }}
                    placeholder="Select Value"
                    maxMenuHeight={220}
                    menuPlacement="auto"
                    isClearable={true}
                    components={{ IndicatorSeparator: () => null }}
                    isDisabled={!filter.attribute_id}
                />
            </div>

            {/* Action Buttons — § 212 grammar: add is the quiet glyph action,
                remove is the row delete. */}
            <div className="tw-w-16 tw-flex tw-gap-2">
                <IconButton
                    variant="subtle"
                    size="sm"
                    onClick={onAdd}
                    data-testid={`add-filter-${index}`}
                    aria-label="Add filter"
                >
                    <Add size={16} aria-hidden="true" />
                </IconButton>
                {canRemove && (
                    <IconButton
                        variant="danger-subtle"
                        size="sm"
                        onClick={onRemove}
                        data-testid={`remove-filter-${index}`}
                        aria-label="Remove filter"
                    >
                        <Minus size={16} aria-hidden="true" />
                    </IconButton>
                )}
            </div>
        </div>
    );
};

export default FilterModal;
